// Pure logic for the onboarding review surface (docs/onboarding-plan.md P0-4).
// Kept separate from the component so the selection/grouping/labelling rules are
// unit-testable without rendering.

// Computed server-side at read time, never stored: this create clashes with
// something the household already has. `blocks` means apply WILL fail this item;
// `warns` means it will succeed and probably isn't what anyone wanted.
export type ItemCollision = {
  kind: string
  severity: 'blocks' | 'warns' | string
  message: string
  existing_id?: string
}

export type ProposalItem = {
  id: string
  seq: number
  op: string
  primitive: string
  depends_on?: string[]
  handle?: string
  collision?: ItemCollision
  risk: 'read' | 'act' | 'sensitive' | string
  why?: string
  selected: boolean
  fields?: Record<string, unknown>
  target?: string
  outcome?: string
  outcome_detail?: string
}

export type Proposal = {
  id: string
  organization_id: string
  intent_kind: string
  intent_target_id?: string
  proposer_kind: string
  rationale?: string
  status: string
  // Which published blueprint this was drawn from, when one was
  // (docs/blueprints.md §9). Provenance only — it records where the household's
  // shape came from and binds nothing to it.
  source_blueprint?: string
  source_blueprint_version?: string
  items: ProposalItem[]
}

// blueprintCredit renders the "drawn from" line, or "" when nothing was.
//
// Shown because it changes how a person reads the rest of the screen: "someone
// worked this pattern out and it has a name and a version" is a different claim
// from "an agent suggested this just now", and the difference is most of why a
// catalog is worth having at all.
// A step waiting on a person (docs/blueprints.md §6). `blocked` is deliberately
// its own outcome: it did not fail and nobody deselected it — somebody has to go
// and do something in the world.
export function isWaitingStep(item: ProposalItem): boolean {
  return item.op === 'advisory' && item.outcome === 'blocked'
}

export function isDoneStep(item: ProposalItem): boolean {
  return item.op === 'advisory' && item.outcome === 'done'
}

// The blueprint author's own instructions for satisfying a requirement. This is
// the payload — they know where the vendor hides the setting, and a household
// reasoning it out fresh would be doing it again worse.
export function stepSetup(item: ProposalItem): string {
  const v = (item.fields ?? {}).setup
  return typeof v === 'string' ? v.trim() : ''
}

export function stepGuidance(item: ProposalItem): string {
  const v = (item.fields ?? {}).guidance
  return typeof v === 'string' ? v.trim() : ''
}

// How many steps are still waiting. Drives "waiting on you" in the queue, so a
// person can see there is something to do without opening it.
export function blueprintCredit(p: Proposal): string {
  if (!p.source_blueprint) return ''
  const name = p.source_blueprint.includes('/')
    ? p.source_blueprint.slice(p.source_blueprint.indexOf('/') + 1).replace(/_/g, ' ')
    : p.source_blueprint
  return p.source_blueprint_version ? `${name} · v${p.source_blueprint_version}` : name
}

// Risk tiers, ordered least → most consequential. The review renders them in
// this order so the safe, pre-selected items read first and the sensitive ones
// (which arrive unselected) stand out at the end.
export const RISK_ORDER = ['read', 'act', 'sensitive'] as const

export function riskMeta(risk: string): { label: string; tone: 'safe' | 'warn' | 'danger' } {
  switch (risk) {
    case 'sensitive':
      return { label: 'Sensitive', tone: 'danger' }
    case 'act':
      return { label: 'Can change things', tone: 'warn' }
    default:
      return { label: 'Read-only', tone: 'safe' }
  }
}

// describeItem turns a raw proposal item into a human label. This is the
// "competence preview" in miniature — say plainly what each item would unlock.
export function describeItem(item: ProposalItem): { title: string; detail?: string } {
  const f = item.fields ?? {}
  if (item.op === 'advisory') {
    return { title: item.why || 'Something for you to do', detail: undefined }
  }
  if (item.op === 'create') {
    const named = (key: string) => (typeof f[key] === 'string' && f[key] ? `: ${f[key]}` : '')
    switch (item.primitive) {
      case 'agent_job_description':
        return { title: `Create the job description${named('title')}`, detail: item.why }
      case 'ai_agent_instance':
        return { title: `Create a new agent${named('name')}`, detail: item.why }
      case 'organization_position':
        return { title: 'Set up the role', detail: item.why }
      case 'working_directory':
        return { title: 'Create its working folder', detail: item.why }
      default:
        return { title: `Create ${item.primitive}`, detail: item.why }
    }
  }
  const removing = item.op === 'revoke'
  if (item.primitive === 'rule') {
    // Display-publish grants read as screen access, not a "tool".
    if (f.resource_kind === 'display') {
      const clearing = f.action === 'display.clear'
      const verb = clearing ? 'clear the display' : 'publish to the display'
      return { title: removing ? `Stop this agent's ability to ${verb}` : `Let this agent ${verb}`, detail: item.why }
    }
    const action = typeof f.action === 'string' && f.action ? f.action : 'a tool'
    if (removing) return { title: `Remove the ${action} tool`, detail: item.why }
    const decision = f.decision === 'deny' ? 'Block' : 'Allow'
    return { title: `${decision} the ${action} tool`, detail: item.why }
  }
  if (item.primitive === 'agent_resource_grant') {
    return {
      title: removing ? 'Remove access to a connected service' : 'Grant access to a connected service',
      detail: item.why,
    }
  }
  if (isIntroduction(item)) {
    return { title: 'Tell the household', detail: item.why }
  }
  if (item.op === 'set' && item.primitive === 'voice_route') {
    return {
      title:
        typeof f.route_position_id === 'string' && f.route_position_id
          ? 'Route the voice device to an agent'
          : 'Clear the voice device’s route',
      detail: item.why,
    }
  }
  return { title: `${item.op} ${item.primitive}`, detail: item.why }
}

// initialSelection seeds the checkbox state from the server's per-item default
// (sensitive items arrive unselected). Advisory items are never "selected" —
// they're manual steps, not machine-appliable.
export function initialSelection(items: ProposalItem[]): Set<string> {
  return new Set(items.filter((i) => i.op !== 'advisory' && i.selected).map((i) => i.id))
}

// Unchecking a create must also uncheck everything built on top of it.
//
// Without this you can drop "create the job description" and leave "create the
// position" checked — which then fails at apply with an unresolved ${jd.id},
// half-building the hire. The person did not ask for that; they asked not to
// create the job description, and the position was only ever there to sit in it.
//
// Re-checking deliberately does NOT re-check dependents: putting something back
// is not a claim about everything that was on top of it, and silently restoring
// a set of items someone had turned off individually would be worse than making
// them tick two boxes.
export function toggle(
  selected: Set<string>,
  id: string,
  items: ProposalItem[] = [],
): Set<string> {
  const next = new Set(selected)
  if (!next.has(id)) {
    next.add(id)
    return next
  }
  next.delete(id)
  for (const dropped of dependentClosure(items, id)) next.delete(dropped)
  return next
}

// Every item that depends, directly or transitively, on the item with this id.
export function dependentClosure(items: ProposalItem[], id: string): Set<string> {
  const byHandle = new Map<string, ProposalItem>()
  for (const i of items) if (i.handle) byHandle.set(i.handle, i)

  const start = items.find((i) => i.id === id)
  if (!start?.handle) return new Set()

  const doomedHandles = new Set([start.handle])
  const out = new Set<string>()
  // Iterate to a fixed point: a dependent's dependents fall too, and the item
  // order gives no guarantee that a parent is seen before its child.
  let grew = true
  while (grew) {
    grew = false
    for (const item of items) {
      if (out.has(item.id) || item.id === id) continue
      if (!(item.depends_on ?? []).some((h) => doomedHandles.has(h))) continue
      out.add(item.id)
      if (item.handle && !doomedHandles.has(item.handle)) {
        doomedHandles.add(item.handle)
        grew = true
      }
    }
  }
  return out
}

// Items whose collision will make apply fail, among those still selected. The
// review surface uses this to say so on the button rather than letting someone
// press Apply and discover a half-built hire.
export function blockingCollisions(
  items: ProposalItem[],
  selected: Set<string>,
): ProposalItem[] {
  return items.filter(
    (i) => selected.has(i.id) && i.collision?.severity === 'blocks',
  )
}

export function applyItemIds(selected: Set<string>): string[] {
  return Array.from(selected)
}

// An introduction is the one item whose effect leaves the system — a person
// reads it (docs/introductions.md §5). It therefore gets its own predicate,
// its own group, and a rendered, EDITABLE body rather than a one-line label:
// approving prose you cannot see is the exact failure the review gate exists to
// prevent, and describeItem's generic fallback would have shown "notify
// introduction" with the text nowhere on screen.
export function isIntroduction(item: ProposalItem): boolean {
  return item.op === 'notify' && item.primitive === 'introduction'
}

export function introductionText(item: ProposalItem): string {
  const t = (item.fields ?? {}).text
  return typeof t === 'string' ? t : ''
}

// Edits the approver made, keyed by item id, narrowed to items still selected —
// an edit to something they then deselected is not sent, and a blank body would
// be refused server-side, so it is dropped here with a clearer outcome: the
// notice simply doesn't go.
export function introductionEdits(
  items: ProposalItem[],
  drafts: Record<string, string>,
  selected: Set<string>,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const item of items) {
    if (!isIntroduction(item) || !selected.has(item.id)) continue
    const draft = drafts[item.id]
    if (draft === undefined) continue
    if (draft.trim() === introductionText(item).trim()) continue
    out[item.id] = draft
  }
  return out
}

export type ItemGroup = { risk: string; items: ProposalItem[] }

// groupByRisk buckets appliable items by risk tier (ordered), with advisory
// items collected into a trailing group of their own.
export function groupByRisk(items: ProposalItem[]): ItemGroup[] {
  const groups: ItemGroup[] = []
  // Entity creations (a new hire) lead — they're the foundation the rest hangs off.
  const create = items.filter((i) => i.op === 'create')
  if (create.length) groups.push({ risk: 'create', items: create })
  // Additions (grants), ordered by risk tier. Creations/revocations/advisories out.
  for (const risk of RISK_ORDER) {
    const g = items.filter(
      (i) =>
        i.op !== 'advisory' &&
        i.op !== 'revoke' &&
        i.op !== 'create' &&
        !isIntroduction(i) &&
        i.risk === risk,
    )
    if (g.length) groups.push({ risk, items: g })
  }
  // Catch any appliable item with an unknown/missing risk tier (e.g. a `set` op
  // like a device route) so it can never fall through the grid invisibly.
  const groupedIds = new Set(groups.flatMap((g) => g.items.map((i) => i.id)))
  const orphan = items.filter(
    (i) =>
      i.op !== 'advisory' &&
      i.op !== 'revoke' &&
      i.op !== 'create' &&
      !isIntroduction(i) &&
      !groupedIds.has(i.id),
  )
  if (orphan.length) {
    const act = groups.find((g) => g.risk === 'act')
    if (act) act.items.push(...orphan)
    else groups.push({ risk: 'act', items: orphan })
  }
  // Removals get their own trailing section, regardless of risk tier — taking
  // access away reads differently from granting it.
  const revoke = items.filter((i) => i.op === 'revoke')
  if (revoke.length) groups.push({ risk: 'revoke', items: revoke })
  // "…and here's what we'll tell everyone" reads last among the things that
  // happen, and just before the things left for a person to do.
  const introduction = items.filter(isIntroduction)
  if (introduction.length) groups.push({ risk: 'introduction', items: introduction })
  const advisory = items.filter((i) => i.op === 'advisory')
  if (advisory.length) groups.push({ risk: 'advisory', items: advisory })
  return groups
}

// Count of items that would actually be applied given a selection.
export function selectedCount(selected: Set<string>): number {
  return selected.size
}
